import { state } from './state.js';
import * as API from './api.js';
import * as UI from './ui.js';
import { TOASTS } from './config/toasts.js';
import { refreshSettingsVisibility, setSettingsOverride } from './workflow_selection.js';
import { deleteSelectedSourcePhoto } from './source_photos.js';
import { JOB_LIMITS } from './config/jobLimits.js';
import { SLIDERS, updateSliderDisplay } from './config/sliders.js';
import { UPLOAD_LIMITS } from './config/uploadLimits.js';
import { UI_COPY } from './config/uiCopy.js';

let origW = 0;
let origH = 0;
let _scaleBase = null;
let _lastScaleClampToastAt = 0;

function _captureScaleBase() {
    if (_scaleBase) return _scaleBase;
    _scaleBase = {
        min: SLIDERS.scale.min,
        max: SLIDERS.scale.max,
        step: SLIDERS.scale.step,
        hint: SLIDERS.scale.hint
    };
    return _scaleBase;
}

function _updateScaleHint(extra) {
    const hintEl = document.getElementById('scaleHint');
    if (!hintEl) return;
    const base = _captureScaleBase();
    if (!extra) {
        hintEl.textContent = base.hint;
        return;
    }
    hintEl.textContent = `${base.hint} ${extra}`;
}

function _getPlanMaxEdgePx() {
    const plan = state.plan || {};
    const raw = _isLegacyHighresDraftEnabled()
        ? Number(plan.legacy_highres_draft_max_long_edge_px || plan.max_export_long_edge_px || 0)
        : Number(plan.max_export_long_edge_px || 0);
    if (!Number.isFinite(raw) || raw <= 0) return null;
    return raw;
}

function _isScaleLocked() {
    const plan = state.plan || {};
    if (_isLegacyHighresDraftEnabled()) return false;
    return Boolean(plan.scale_locked);
}

function _isLegacyHighresDraftEnabled() {
    const toggle = document.getElementById('legacyHighresDraftToggle');
    return Boolean(toggle && toggle.checked);
}

function _computeMaxScaleByPixels(width, height) {
    const w = Number(width || 0);
    const h = Number(height || 0);
    if (!w || !h) return null;
    const maxPixels = Number(JOB_LIMITS.MAX_OUTPUT_PIXELS || 0);
    if (!maxPixels) return null;
    const sourcePixels = w * h;
    if (!sourcePixels) return null;
    return Math.sqrt(maxPixels / sourcePixels);
}

function _computeMaxScaleByPlan(width, height) {
    const w = Number(width || 0);
    const h = Number(height || 0);
    if (!w || !h) return null;
    const planMaxEdge = _getPlanMaxEdgePx();
    if (!planMaxEdge) return null;
    const sourceEdge = Math.max(w, h);
    if (!sourceEdge) return null;
    return planMaxEdge / sourceEdge;
}


function _clampScaleInput(reason = '') {
    const scaleInput = document.getElementById('scaleInput');
    if (!scaleInput) return false;
    const min = Number(scaleInput.min || 0);
    const max = Number(scaleInput.max || 0);
    let value = Number(scaleInput.value || 0);
    if (!max || value <= max && value >= min) return false;
    if (max && value > max) value = max;
    if (min && value < min) value = min;
    scaleInput.value = String(value);
    updateSliderDisplay('scaleInput', value);
    const now = Date.now();
    if (now - _lastScaleClampToastAt > 1500 && reason) {
        _lastScaleClampToastAt = now;
        UI.showToast('Scale capped', reason, 3200);
    }
    return true;
}

function _applyDynamicScaleLimit(width, height) {
    const base = _captureScaleBase();
    const scaleInput = document.getElementById('scaleInput');
    if (!scaleInput) return;
    const computedMaxPixels = _computeMaxScaleByPixels(width, height);
    const computedMaxPlan = _computeMaxScaleByPlan(width, height);
    let computedMax = computedMaxPixels;
    if (computedMaxPlan && Number.isFinite(computedMaxPlan)) {
        if (!computedMax || !Number.isFinite(computedMax)) {
            computedMax = computedMaxPlan;
        } else {
            computedMax = Math.min(computedMax, computedMaxPlan);
        }
    }
    if (!computedMax || !Number.isFinite(computedMax)) {
        _resetScaleLimit();
        return;
    }

    let step = Number(base.step || 0.1) || 0.1;
    let maxScale = Math.min(base.max, computedMax);
    if (maxScale >= step) {
        maxScale = Math.floor(maxScale / step) * step;
    }
    if (maxScale <= 0) {
        maxScale = computedMax;
    }
    let minScale = base.min;
    if (computedMaxPlan && Number.isFinite(computedMaxPlan) && computedMaxPlan < minScale) {
        minScale = computedMaxPlan;
    }
    if (maxScale < minScale) {
        minScale = maxScale;
    }

    const planMaxEdge = _getPlanMaxEdgePx();

    scaleInput.min = String(minScale);
    scaleInput.max = String(maxScale);
    scaleInput.step = String(step);
    const limitMp = (JOB_LIMITS.MAX_OUTPUT_PIXELS / 1_000_000).toFixed(1);
    let hint = `Max for this image: ${maxScale.toFixed(2)}x (limit ${limitMp} MP).`;
    if (planMaxEdge) {
        hint = `Max for this image: ${maxScale.toFixed(2)}x (plan cap ${planMaxEdge}px).`;
    }

    if (_isScaleLocked() && computedMaxPlan && Number.isFinite(computedMaxPlan)) {
        const lockedScale = Math.max(0.01, computedMaxPlan);
        scaleInput.min = String(lockedScale);
        scaleInput.max = String(lockedScale);
        scaleInput.step = String(Math.max(step, 0.01));
        scaleInput.value = String(lockedScale);
        scaleInput.disabled = true;
        updateSliderDisplay('scaleInput', lockedScale);
        _updateScaleHint(`Fixed at ${lockedScale.toFixed(2)}x (plan cap ${planMaxEdge}px).`);
        UI.updateStats(origW, origH);
        return;
    }

    scaleInput.disabled = false;
    _updateScaleHint(hint);

    const clamped = _clampScaleInput(`Max scale for this image is ${maxScale.toFixed(1)}x.`);
    if (clamped) {
        UI.updateStats(origW, origH);
    }
}

function _resetScaleLimit() {
    const base = _captureScaleBase();
    const scaleInput = document.getElementById('scaleInput');
    if (!scaleInput) return;
    scaleInput.min = String(base.min);
    scaleInput.max = String(base.max);
    scaleInput.step = String(base.step);
    scaleInput.disabled = false;
    _updateScaleHint('');
}

function _resetTargetState(localUrl = '') {
    origW = 0;
    origH = 0;
    state.targetFilename = "";
    state.targetJobId = null;
    state.selectedSourcePhotoId = null;
    state.selectedSourcePhoto = null;
    _resetScaleLimit();
    _setPreview('');

    const fileInput = document.getElementById('targetFile');
    if (fileInput) fileInput.value = "";

    if (localUrl) {
        try { URL.revokeObjectURL(localUrl); } catch (e) {}
    }

    setSettingsOverride(false);
    refreshSettingsVisibility();
}

function _formatMb(bytes) {
    return (Number(bytes || 0) / (1024 * 1024)).toFixed(1);
}

async function _loadImageDimensions(localUrl) {
    return new Promise((resolve) => {
        if (!localUrl) return resolve(null);
        const img = new Image();
        img.onload = () => resolve({ width: img.width, height: img.height });
        img.onerror = () => resolve(null);
        img.src = localUrl;
    });
}

function _getMainPhotoPlaceholderHtml() {
    const hasUploadedPhotos = Boolean(
        !state.isAnonymous &&
        Array.isArray(state.sourcePhotos) &&
        state.sourcePhotos.length > 0
    );
    const line = hasUploadedPhotos
        ? 'Upload a Main Photo or choose it from Uploaded Photos.'
        : 'Upload a Main Photo.';
    return `
        <div style="font-size:34px; margin-bottom:10px;">📷</div>
        <div><b>${line}</b></div>
    `;
}

export function getOriginalSize() {
    return { origW, origH };
}

export function setOriginalSize(width, height) {
    const w = Number(width || 0);
    const h = Number(height || 0);
    origW = w;
    origH = h;
    const origSize = document.getElementById('origSize');
    if (origSize) {
        origSize.innerText = (origW && origH) ? `${origW} x ${origH} px` : '-';
    }
    const targetDrop = document.getElementById('targetDrop');
    if (targetDrop && origW && origH) {
        targetDrop.style.aspectRatio = `${origW} / ${origH}`;
    } else if (targetDrop) {
        targetDrop.style.removeProperty('aspect-ratio');
    }
    if (origW && origH) {
        _applyDynamicScaleLimit(origW, origH);
    } else {
        _resetScaleLimit();
    }
    UI.updateStats(origW, origH);
}

function _setPreview(sourceUrl) {
    const preview = document.getElementById('sourcePreview');
    const placeholder = document.getElementById('targetDropLabel');
    const overlay = document.getElementById('targetDropOverlay');
    const targetDrop = document.getElementById('targetDrop');

    if (preview) {
        preview.src = sourceUrl || '';
        preview.classList.toggle('hidden', !sourceUrl);
        preview.classList.remove('img-loading');
    }
    if (placeholder) {
        if (sourceUrl) {
            placeholder.classList.add('hidden');
        } else {
            placeholder.classList.remove('hidden');
            placeholder.innerHTML = _getMainPhotoPlaceholderHtml();
        }
    }
    if (overlay) {
        overlay.classList.toggle('hidden', !sourceUrl);
        if (sourceUrl) {
            overlay.innerHTML = '<span class="overlay-chip">↻ Change photo</span>';
        }
    }
    const deleteBtn = document.getElementById('targetDeleteBtn');
    if (deleteBtn) {
        deleteBtn.classList.toggle('hidden', !sourceUrl);
    }
    if (!sourceUrl && targetDrop) {
        targetDrop.style.removeProperty('aspect-ratio');
        targetDrop.classList.remove('is-loading');
    }
}

export function refreshTargetDropLimitHint() {
    const placeholder = document.getElementById('targetDropLabel');
    if (!placeholder) return;
    const preview = document.getElementById('sourcePreview');
    const hasPreview = Boolean(
        preview && !preview.classList.contains('hidden') && String(preview.src || '').trim()
    );
    if (!hasPreview) {
        placeholder.innerHTML = _getMainPhotoPlaceholderHtml();
    }
}

export function initTargetHandlers() {
    const scaleInput = document.getElementById('scaleInput');
    if (scaleInput) {
        scaleInput.addEventListener('input', () => {
            _clampScaleInput(`Max scale for this image is ${Number(scaleInput?.max || 0).toFixed(2)}x.`);
            UI.updateStats(origW, origH);
        });
    }
    const legacyToggle = document.getElementById('legacyHighresDraftToggle');
    if (legacyToggle) {
        legacyToggle.addEventListener('change', () => {
            if (origW && origH) {
                _applyDynamicScaleLimit(origW, origH);
            } else {
                _resetScaleLimit();
            }
            UI.updateStats(origW, origH);
        });
    }
    document.getElementById('thInput').addEventListener('input', () => UI.updateStats(origW, origH));
    document.getElementById('targetFile').addEventListener('change', e => handleTarget(e.target.files[0]));

    const targetDrop = document.getElementById('targetDrop');
    const targetFile = document.getElementById('targetFile');
    targetDrop.addEventListener('dragover', (e) => {
        e.preventDefault();
        targetDrop.classList.add('is-dragover');
    });
    targetDrop.addEventListener('dragleave', () => {
        targetDrop.classList.remove('is-dragover');
    });
    targetDrop.addEventListener('drop', (e) => {
        e.preventDefault();
        targetDrop.classList.remove('is-dragover');
        handleTarget(e.dataTransfer.files[0]);
    });
    targetDrop.addEventListener('click', (e) => {
        const deleteBtn = e.target && e.target.closest ? e.target.closest('.source-preview-delete') : null;
        if (deleteBtn) {
            e.preventDefault();
            e.stopPropagation();
            deleteSelectedSourcePhoto();
            return;
        }
        if (e.target !== targetFile) targetFile.click();
    });
}

export function applyPlanLimits(plan) {
    if (plan) {
        state.plan = plan;
    }
    if (origW && origH) {
        _applyDynamicScaleLimit(origW, origH);
    }
}

export async function handleTarget(file) {
    if (!file) return;

    if (UPLOAD_LIMITS.MAX_SIZE_BYTES && file.size > UPLOAD_LIMITS.MAX_SIZE_BYTES) {
        const msg = UI_COPY.uploads.fileTooLarge(
            _formatMb(file.size),
            _formatMb(UPLOAD_LIMITS.MAX_SIZE_BYTES)
        );
        UI.showToast('Upload too large', msg, 4000);
        API.logEvent('target_upload_precheck_reject', {
            reason: 'size',
            size_mb: Number(_formatMb(file.size)),
            limit_mb: Number(_formatMb(UPLOAD_LIMITS.MAX_SIZE_BYTES))
        }).catch(() => {});
        API.logUiFlow('main_photo', 'upload', 'blocked', {
            reason: 'size_limit',
        }).catch(() => {});
        _resetTargetState();
        return;
    }

    // Optimistic preview (local blob) for immediate feedback.
    const localUrl = URL.createObjectURL(file);
    _setPreview(localUrl);

    const dims = await _loadImageDimensions(localUrl);
    if (dims && dims.width && dims.height) {
        const pixels = dims.width * dims.height;
        if (UPLOAD_LIMITS.MAX_TARGET_PIXELS && pixels > UPLOAD_LIMITS.MAX_TARGET_PIXELS) {
            const msg = UI_COPY.uploads.imageTooLarge(
                (pixels / 1_000_000).toFixed(1),
                (UPLOAD_LIMITS.MAX_TARGET_PIXELS / 1_000_000).toFixed(0)
            );
            UI.showToast('Upload too large', msg, 4000);
            API.logEvent('target_upload_precheck_reject', {
                reason: 'pixels',
                pixels,
                max_pixels: UPLOAD_LIMITS.MAX_TARGET_PIXELS
            }).catch(() => {});
            API.logUiFlow('main_photo', 'upload', 'blocked', {
                reason: 'pixel_limit',
            }).catch(() => {});
            _resetTargetState(localUrl);
            return;
        }
        setOriginalSize(dims.width, dims.height);
    }

    const uploadName = file && file.name ? file.name : 'photo';
    const uploadingToast = UI.showToast('Uploading main photo', `Currently uploading: ${uploadName}`, 0);

    try {
        const data = await API.uploadTarget(file);
        if (uploadingToast) uploadingToast.close();
        if (data.status === 'ok') {
            state.targetFilename = data.filename || '';
            state.jobId = data.jobId || state.jobId;
            state.targetJobId = data.jobId || state.targetJobId;
            state.selectedSourcePhotoId = data.source_photo?.id || state.selectedSourcePhotoId;
            state.selectedSourcePhoto = data.source_photo ? { ...data.source_photo } : state.selectedSourcePhoto;
            setSettingsOverride(false);

            const previewUrl = data.source_url || data.source_photo?.full_url || '';
            _setPreview(previewUrl);
            setOriginalSize(data.width, data.height);
            refreshSettingsVisibility();

            document.dispatchEvent(new CustomEvent('source:uploaded', { detail: data }));
            try { URL.revokeObjectURL(localUrl); } catch (e) {}

            if (data.dedup_hit) {
                if (data.restored) {
                    UI.showToast('Main photo restored', 'Previously deleted photo restored.', 3500);
                } else {
                    UI.showToast('Main photo reused', 'Already uploaded. Using your existing photo.', 3500);
                }
            } else {
                UI.showToast('Main photo uploaded', `${uploadName} uploaded.`, 2500);
            }
            API.logUiFlow('main_photo', 'upload', 'ok', {
                source_id: data.source_photo?.id || '',
                dedup_hit: Boolean(data.dedup_hit),
                restored: Boolean(data.restored),
            }).catch(() => {});
            const fileInput = document.getElementById('targetFile');
            if (fileInput) fileInput.value = '';
        }
    } catch (error) {
        console.error("Target Upload Failed:", error);
        if (uploadingToast) uploadingToast.close();
        const toast = TOASTS.uploadFailed(error.message);
        UI.showToast(toast.title, toast.message, toast.duration);
        API.logUiFlow('main_photo', 'upload', 'error', {
            error: String(error && error.message ? error.message : error),
        }).catch(() => {});
        _resetTargetState(localUrl);
    }
}
